/**
 * The wire contract of the «Планы» registry — every type here must match
 * `data/registry-seed.json` field for field (that file is the reference
 * payload, produced by `tools/migrate-backup.mjs`).
 *
 * The whole registry lives in ONE versioned document under the Valkey key
 * `plans:registry` (LLM_CONTEXT/02-architecture.md §2.2); the audit trail
 * lives in the `plans:audit` list and is only *shipped* next to the document
 * inside the seed file.
 *
 * House convention: types + const tuples only, no runtime logic. The label
 * maps, the priority rank and the enum guards below are const data (and
 * trivial derivations of it) — nothing more.
 */
/**
 * The seven built-in status ids in their CANONICAL order — the seed of the
 * dictionary and the fallback sort order when no registry is at hand.
 */
export const PROJECT_STATUSES = [
    'new',
    'agreed',
    'in_work',
    'on_support',
    'deferred',
    'done',
    'rejected',
];
/** Default Russian labels of the BUILT-IN statuses, in `PROJECT_STATUSES` order. */
export const PROJECT_STATUS_LABELS = {
    new: 'Новый',
    agreed: 'Согласован',
    in_work: 'В работе',
    on_support: 'На поддержке',
    deferred: 'Отложен',
    done: 'Сделано',
    rejected: 'Отклонён',
};
/**
 * Tolerant label lookup for ANY status id: the dictionary label when the id is
 * one of the built-ins, otherwise the id itself — an unknown id must still
 * display, never render as `undefined`.
 */
export function defaultStatusLabel(statusId) {
    return PROJECT_STATUS_LABELS[statusId] ?? statusId;
}
/** The seven built-ins as the seed payload (`system: true`, canonical order). */
export const DEFAULT_STATUSES = PROJECT_STATUSES.map((id) => ({
    id,
    label: PROJECT_STATUS_LABELS[id],
    system: true,
}));
/** Priority (F-02) — one enum drives all THREE queues (F-17: stream / person / team). */
export const PROJECT_PRIORITIES = ['urgent', 'high', 'medium', 'low'];
/** Russian labels, in the same order as `PROJECT_PRIORITIES`. */
export const PROJECT_PRIORITY_LABELS = {
    urgent: 'Срочно',
    high: 'Высокий',
    medium: 'Средний',
    low: 'Низкий',
};
/**
 * Sorting rank used by every queue view and by the "best value wins" rule for
 * a project that appears several times inside one team queue (spec §1.3).
 */
export function priorityRank(priority) {
    return PROJECT_PRIORITIES.indexOf(priority);
}
/** `true` when the value is one of the seven statuses (soft-normalisation / DTO checks). */
export function isProjectStatus(value) {
    return typeof value === 'string' && PROJECT_STATUSES.includes(value);
}
/** `true` when the value is one of the four priorities. */
export function isPriority(value) {
    return typeof value === 'string' && PROJECT_PRIORITIES.includes(value);
}
//# sourceMappingURL=registry.js.map